import { useCallback, useState, type ReactNode } from "react";
import type { ConfirmDialogTone } from "../components/layout/ConfirmDialog";

export interface ConfirmOptions {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmDialogTone;
}

interface ConfirmState extends ConfirmOptions {
  open: boolean;
}

const CLOSED: ConfirmState = {
  open: false,
  title: "",
  message: "",
};

/**
 * Imperative confirm() that opens the shared ConfirmDialog instead of window.confirm.
 */
export function useConfirmDialog() {
  const [state, setState] = useState<ConfirmState>(CLOSED);
  const [resolver, setResolver] = useState<((value: boolean) => void) | null>(
    null
  );

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setResolver(() => resolve);
      setState({ open: true, ...options });
    });
  }, []);

  const handleClose = useCallback(
    (result: boolean) => {
      setState(CLOSED);
      resolver?.(result);
      setResolver(null);
    },
    [resolver]
  );

  const dialogProps = {
    open: state.open,
    title: state.title,
    message: state.message,
    confirmLabel: state.confirmLabel,
    cancelLabel: state.cancelLabel,
    tone: state.tone,
    onConfirm: () => handleClose(true),
    onCancel: () => handleClose(false),
  };

  return { confirm, dialogProps };
}
